/**
 * services/SessionDraftService.ts
 * Session draft management service with sync-aware operations
 *
 * Provides CRUD operations for SessionDraft entities with proper sync
 * operation logging. Unlike BaseService, this service handles SessionDraft's
 * unique structure where timestamps live in metadata rather than root level.
 *
 * All write methods wrap operations in transactions with sync tables and
 * log operations for cross-device synchronization.
 */

// External imports
import { db } from '@/lib/db';

// Sync utilities
import { softDeleteRecord } from '@/services/sync/SoftDeleteUtils';
import { markAsInstrumentedWriteContext } from '@/services/sync/SyncImportContext';
import { getSyncOperationService } from '@/services/sync/SyncOperationService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import { buildSetOps, buildDiffOps } from '@/services/sync/OperationBuilder';

// Factory and utility functions
import { createEmptySessionDraft, buildPreTradingConfigSnapshot } from '@/components/TradingHub/utils/sessionDraftUtils';

// Settings services
import { preTradingSettingsService } from '@/services/PreTradingSettingsService';
import { tradingSetupSettingsService, extractTradingSetupDefaults } from '@/services/TradingSetupSettingsService';
import { accountService } from '@/services/AccountService';

// Utilities
import { isTombstoned } from '@/utils/tombstone';
import { generateUUID } from '@/utils/uuid';

// Type imports
import type { SessionDraft, TimedPhaseStatus } from '@/types/transient/SessionDraft';
import type { TradeDraft, TradeSetupDraft } from '@/types/transient/TradeDraft';
import type { NoteDraft } from '@/types/transient/NoteDraft';
import type { StrategyPlaybookPair } from '@/types/shared/TradingSetupTypes';
import type {
  TradingSetupRiskSnapshot,
  SessionStrategyOverride,
  SessionPlaybookOverride,
} from '@/types/schema/TradingSetupSnapshots';

// Service imports (carry-forward)
import { retiredDraftLineageService } from './RetiredDraftLineageService';
import type { ObjectiveDraft } from '@/types/transient/ObjectiveDraft';
import type { EconomicEventDraft } from '@/types/transient/EconomicEventDraft';
import type { MarketStateDraft } from '@/types/transient/MarketStateDraft';
import type { PreTradingSettings, PreTradingSectionConfig } from '@/types/schema/PreTradingSettings';

/**
 * Service for managing SessionDraft entities with sync-aware operations
 *
 * Does NOT extend BaseService because:
 * 1. SessionDraft uses metadata-based timestamps, not entity-level
 * 2. updatePersonalPrep is a partial update (only two fields)
 * 3. Direct table access gives better control over transaction boundaries
 */
class SessionDraftService {
  private readonly table = db.session_drafts;

  /**
   * Get a session draft by ID
   *
   * @param draftId - The draft ID to look up
   * @returns The SessionDraft if found, null otherwise
   */
  async getById(draftId: string): Promise<SessionDraft | null> {
    const draft = await this.table.get(draftId);
    return draft ?? null;
  }

  /**
   * Create a new session draft with sync operation logging
   *
   * @param sessionType - The type of trading session
   * @param date - The trading date in YYYY-MM-DD format
   * @returns The created SessionDraft
   */
  async create(
    sessionType: SessionDraft['sessionType'],
    date: string
  ): Promise<SessionDraft> {
    // Load current settings to snapshot into draft
    const settings = await preTradingSettingsService.getOrCreateDefaultSettings();
    const tradingSetupSettings = await tradingSetupSettingsService.getCurrentSettings();
    const tradingSetupDefaults = extractTradingSetupDefaults(tradingSetupSettings);

    // Resolve effective defaultAccountId for single-account users.
    // Settings may store undefined when the user never explicitly configured a default,
    // but divergence detection resolves to the effective account. Without this resolution,
    // brand new drafts immediately appear mismatched against resolved settings.
    if (!tradingSetupDefaults.defaultAccountId) {
      const activeAccounts = await accountService.getActiveAccounts();
      const singleAccount = activeAccounts.length === 1 ? activeAccounts[0] : undefined;
      if (singleAccount) {
        tradingSetupDefaults.defaultAccountId = singleAccount.id;
      }
    }

    const newDraft = createEmptySessionDraft(sessionType, date, settings, tradingSetupDefaults);

    // Wrap in transaction with op logging for proper sync
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state, db.trading_setup_settings],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.add(newDraft);

        // Log insert operations for sync
        const ops = buildSetOps(
          'session_drafts',
          newDraft.id,
          newDraft as unknown as Record<string, unknown>
        );
        if (ops.length > 0) {
          await getSyncOperationService().logBatch(ops);
        }
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', newDraft.id);

    return newDraft;
  }

  /**
   * Soft delete a session draft (tombstone pattern for sync)
   *
   * @param draftId - The draft ID to delete
   * @returns true if deleted, false if not found or already deleted
   */
  async softDelete(draftId: string): Promise<boolean> {
    let wasDeleted = false;

    try {
      // Wrap in transaction with op logging for proper sync
      await db.transaction(
        'rw',
        [db.session_drafts, db.sync_operations, db.sync_state],
        async () => {
          markAsInstrumentedWriteContext();
          const result = await softDeleteRecord('session_drafts', draftId, {
            notify: false,
          });
          wasDeleted = result.deleted;

          if (wasDeleted) {
            await getSyncOperationService().logOperation({
              table: 'session_drafts',
              recordId: draftId,
              op: 'delete',
            });
          }
        }
      );

      // Only notify if deletion actually occurred
      if (wasDeleted) {
        syncCoordinatorService.notifyChange('session_drafts', draftId);
      }

      return wasDeleted;
    } catch (error) {
      // softDeleteRecord throws if record not found - return false per contract
      if (error instanceof Error && error.message.includes('Record not found')) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Update personal preparation data on a session draft
   *
   * Uses diff-based operation logging to only record actual changes.
   * Skips write entirely if no changes detected (avoids sync churn).
   *
   * @param draftId - The draft ID to update
   * @param healthMetrics - Health metrics values (metric id -> value)
   * @param personalPrep - Personal prep field values (field id -> value)
   * @returns true if update succeeded or no-op, false if draft not found
   */
  async updatePersonalPrep(
    draftId: string,
    healthMetrics: Record<string, number> | undefined,
    personalPrep: Record<string, boolean | string | number> | undefined
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      // Draft not found or was soft-deleted - return false to trigger redirect
      return false;
    }

    // Build candidate "after" draft by spreading ALL existing fields
    // then merging only the two sections we're updating
    // Note: We must handle undefined specially for exactOptionalPropertyTypes:
    // - If value is defined, include it
    // - If value is undefined, we want to CLEAR it (omit the property)
    // Start with existing draft, then explicitly set or delete the fields
    const candidateAfter: SessionDraft = { ...existingDraft };
    if (healthMetrics !== undefined) {
      candidateAfter.healthMetrics = healthMetrics;
    } else {
      delete candidateAfter.healthMetrics;
    }
    if (personalPrep !== undefined) {
      candidateAfter.personalPrep = personalPrep;
    } else {
      delete candidateAfter.personalPrep;
    }

    // For buildDiffOps, we need to explicitly mark cleared sections as null
    // so the diff engine generates clear ops for syncing to other devices.
    // Using a separate object for diffing to avoid TypeScript issues with null.
    const afterForDiff: Record<string, unknown> = {
      ...(candidateAfter as unknown as Record<string, unknown>),
    };
    // If clearing healthMetrics (was defined, now undefined), set null for diff
    if (healthMetrics === undefined && existingDraft.healthMetrics !== undefined) {
      afterForDiff['healthMetrics'] = null;
    }
    // If clearing personalPrep (was defined, now undefined), set null for diff
    if (personalPrep === undefined && existingDraft.personalPrep !== undefined) {
      afterForDiff['personalPrep'] = null;
    }

    // Compute diff BEFORE touching metadata to avoid false positives
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      afterForDiff
    );

    // If no actual changes, return early (skip write entirely)
    if (ops.length === 0) {
      return true;
    }

    // Since there ARE data changes, also sync metadata.updatedAt
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Update objectives array on a session draft
   *
   * Uses diff-based operation logging to only record actual changes.
   * Skips write entirely if no changes detected (avoids sync churn).
   * Always sets objectives to the provided array (never deletes the field).
   *
   * @param draftId - The draft ID to update
   * @param objectives - The new objectives array (can be empty)
   * @returns true if update succeeded or no-op, false if draft not found
   */
  async updateObjectives(
    draftId: string,
    objectives: ObjectiveDraft[]
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      // Draft not found or was soft-deleted - return false to trigger redirect
      return false;
    }

    // Build candidate draft with new objectives array
    const candidateAfter: SessionDraft = {
      ...existingDraft,
      objectives,
    };

    // For diff computation, treat undefined as [] to handle legacy drafts
    const existingForDiff: Record<string, unknown> = {
      ...(existingDraft as unknown as Record<string, unknown>),
      objectives: existingDraft.objectives ?? [],
    };

    // Compute diff BEFORE touching metadata to avoid false positives
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingForDiff,
      candidateAfter as unknown as Record<string, unknown>
    );

    // If no actual changes, return early (skip write entirely)
    if (ops.length === 0) {
      return true;
    }

    // Since there ARE data changes, also sync metadata.updatedAt
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Updates the economic events array in a SessionDraft.
   * Used by PreTradingPhase auto-save for economic calendar step.
   *
   * If the events array hasn't changed, returns true without write/notify (no-op).
   *
   * @param draftId - The draft ID to update
   * @param economicEvents - The new economic events array (can be empty)
   * @returns true if update succeeded or no-op, false if draft not found
   */
  async updateEconomicEvents(
    draftId: string,
    economicEvents: EconomicEventDraft[]
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      // Draft not found or was soft-deleted - return false to trigger redirect
      return false;
    }

    // Build candidate draft with new economicEvents array
    const candidateAfter: SessionDraft = {
      ...existingDraft,
      economicEvents,
    };

    // For diff computation, treat undefined as [] to handle legacy drafts
    const existingForDiff: Record<string, unknown> = {
      ...(existingDraft as unknown as Record<string, unknown>),
      economicEvents: existingDraft.economicEvents ?? [],
    };

    // Compute diff BEFORE touching metadata to avoid false positives
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingForDiff,
      candidateAfter as unknown as Record<string, unknown>
    );

    // If no actual changes, return early (skip write entirely)
    if (ops.length === 0) {
      return true;
    }

    // Since there ARE data changes, also sync metadata.updatedAt
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Updates the trades array in a SessionDraft.
   * Used by the Trades pane auto-save when a trade is created, edited, or shelved.
   *
   * If the trades array hasn't changed, returns true without write/notify (no-op).
   *
   * @param draftId - The draft ID to update
   * @param trades - The new trades array (can be empty)
   * @returns true if update succeeded or no-op, false if draft not found
   */
  async updateTrades(draftId: string, trades: TradeDraft[]): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      // Draft not found or was soft-deleted - return false to trigger redirect
      return false;
    }

    // Build candidate draft with new trades array
    const candidateAfter: SessionDraft = {
      ...existingDraft,
      trades,
    };

    // For diff computation, treat undefined as [] to handle legacy drafts
    const existingForDiff: Record<string, unknown> = {
      ...(existingDraft as unknown as Record<string, unknown>),
      trades: existingDraft.trades ?? [],
    };

    // Compute diff BEFORE touching metadata to avoid false positives
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingForDiff,
      candidateAfter as unknown as Record<string, unknown>
    );

    // If no actual changes, return early (skip write entirely)
    if (ops.length === 0) {
      return true;
    }

    // Since there ARE data changes, also sync metadata.updatedAt
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Updates the final checks completion array in a SessionDraft.
   * Used by PreTradingPhase auto-save for final checks step.
   *
   * If the finalChecks array hasn't changed, returns true without write/notify (no-op).
   *
   * @param draftId - The draft ID to update
   * @param finalChecks - The new final checks array (can be empty or undefined)
   * @returns true if update succeeded or no-op, false if draft not found
   */
  async updateFinalChecks(
    draftId: string,
    finalChecks: SessionDraft['finalChecks']
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      // Draft not found or was soft-deleted - return false to trigger redirect
      return false;
    }

    // Build candidate draft with new finalChecks array
    // Use conditional spread to satisfy exactOptionalPropertyTypes
    const candidateAfter: SessionDraft = {
      ...existingDraft,
      ...(finalChecks !== undefined ? { finalChecks } : {}),
    };

    // If finalChecks is undefined/empty, remove from draft (don't store empty array)
    if (!finalChecks || finalChecks.length === 0) {
      delete (candidateAfter as Partial<SessionDraft>).finalChecks;
    }

    // For diff computation, treat undefined as [] to handle legacy drafts
    const existingForDiff: Record<string, unknown> = {
      ...(existingDraft as unknown as Record<string, unknown>),
      finalChecks: existingDraft.finalChecks ?? [],
    };

    const candidateForDiff: Record<string, unknown> = {
      ...(candidateAfter as unknown as Record<string, unknown>),
      finalChecks: candidateAfter.finalChecks ?? [],
    };

    // Compute diff BEFORE touching metadata to avoid false positives
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingForDiff,
      candidateForDiff
    );

    // If no actual changes, return early (skip write entirely)
    if (ops.length === 0) {
      return true;
    }

    // Since there ARE data changes, also sync metadata.updatedAt
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Updates the pre-trading config snapshot and migrates values.
   * Used when user chooses "Update draft" in the Settings Changed modal.
   *
   * Migrates existing values by filtering to only IDs that exist in the new config.
   * If migrated map becomes empty, the root key is deleted (treat {} as undefined).
   * If no effective changes, returns true without write/notify (no-op).
   *
   * @param draftId - The draft ID to update
   * @param newSettings - The current PreTradingSettings to snapshot
   * @returns true if update succeeded or no-op, false if draft not found or tombstoned
   */
  async updatePreTradingConfig(
    draftId: string,
    newSettings: PreTradingSettings
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      return false;
    }

    // Build new config snapshot from current settings
    const newConfig = buildPreTradingConfigSnapshot(newSettings);

    // Migrate healthMetrics: keep only IDs that exist in new config
    const newMetricIds = new Set(newConfig.healthMetrics.metrics.map(m => m.id));
    const existingHealthMetrics = existingDraft.healthMetrics ?? {};
    const migratedHealthMetrics = Object.fromEntries(
      Object.entries(existingHealthMetrics).filter(([id]) => newMetricIds.has(id))
    );

    // Migrate personalPrep: keep only IDs that exist in new config AND have same type
    // Type changes invalidate values (e.g., text → dropdown makes old value invalid)
    // For dropdowns, also check that the stored option ID still exists in new options
    const newFieldsById = new Map(newConfig.personalPrepFields.map(f => [f.id, f]));
    // Note: preTradingConfig should exist (legacy fallback applies on load before this is called)
    const oldFieldsById = new Map(
      (existingDraft.preTradingConfig?.personalPrepFields ?? []).map(f => [f.id, f])
    );
    const existingPersonalPrep = existingDraft.personalPrep ?? {};
    const migratedPersonalPrep = Object.fromEntries(
      Object.entries(existingPersonalPrep).filter(([id, value]) => {
        const newField = newFieldsById.get(id);
        const oldField = oldFieldsById.get(id);
        // Keep value only if field exists in new config AND type hasn't changed
        if (!newField || !oldField || newField.type !== oldField.type) {
          return false;
        }
        // For dropdowns, check that the stored option ID still exists
        if (newField.type === 'dropdown' && typeof value === 'string') {
          const newOptionIds = new Set((newField.options ?? []).map(o => o.id));
          if (!newOptionIds.has(value)) {
            return false;
          }
        }
        return true;
      })
    );

    // Build candidate draft with new config + migrated values
    // Delete root key if migrated map is empty (treat {} as undefined)
    const candidateAfter: SessionDraft = { ...existingDraft };
    candidateAfter.preTradingConfig = newConfig;

    if (Object.keys(migratedHealthMetrics).length > 0) {
      candidateAfter.healthMetrics = migratedHealthMetrics;
    } else {
      delete candidateAfter.healthMetrics;
    }

    if (Object.keys(migratedPersonalPrep).length > 0) {
      candidateAfter.personalPrep = migratedPersonalPrep;
    } else {
      delete candidateAfter.personalPrep;
    }

    // For buildDiffOps, handle cleared sections as null for proper sync
    const afterForDiff: Record<string, unknown> = {
      ...(candidateAfter as unknown as Record<string, unknown>),
    };
    // If clearing healthMetrics (was defined, now empty), set null for diff
    if (
      Object.keys(migratedHealthMetrics).length === 0 &&
      existingDraft.healthMetrics !== undefined
    ) {
      afterForDiff['healthMetrics'] = null;
    }
    // If clearing personalPrep (was defined, now empty), set null for diff
    if (
      Object.keys(migratedPersonalPrep).length === 0 &&
      existingDraft.personalPrep !== undefined
    ) {
      afterForDiff['personalPrep'] = null;
    }

    // Compute diff BEFORE touching metadata to avoid false positives
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      afterForDiff
    );

    // If no actual changes, return early (no-op)
    if (ops.length === 0) {
      return true;
    }

    // Since there ARE data changes, also sync metadata.updatedAt
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Sets the health metrics ack hash on a draft.
   * Called when user acknowledges settings mismatch via "Settings Changed" modal.
   *
   * If the hash is already equal to the stored value, returns true without write/notify (no-op).
   *
   * @param draftId - The draft ID to update
   * @param hash - The settings hash to store
   * @returns true if update succeeded or no-op, false if draft not found or tombstoned
   */
  async setHealthMetricsAckHash(draftId: string, hash: string): Promise<boolean> {
    return this._setAckHashField(draftId, 'healthMetricsAckHash', hash);
  }

  /**
   * Sets the personal prep ack hash on a draft.
   * Called when user acknowledges settings mismatch via "Settings Changed" modal.
   *
   * If the hash is already equal to the stored value, returns true without write/notify (no-op).
   *
   * @param draftId - The draft ID to update
   * @param hash - The settings hash to store
   * @returns true if update succeeded or no-op, false if draft not found or tombstoned
   */
  async setPersonalPrepAckHash(draftId: string, hash: string): Promise<boolean> {
    return this._setAckHashField(draftId, 'personalPrepAckHash', hash);
  }

  /**
   * Sets the section visibility ack hash on a draft.
   * Called when user acknowledges settings mismatch via "Settings Changed" modal.
   *
   * If the hash is already equal to the stored value, returns true without write/notify (no-op).
   *
   * @param draftId - The draft ID to update
   * @param hash - The settings hash to store
   * @returns true if update succeeded or no-op, false if draft not found or tombstoned
   */
  async setSectionVisibilityAckHash(draftId: string, hash: string): Promise<boolean> {
    return this._setAckHashField(draftId, 'sectionVisibilityAckHash', hash);
  }

  /**
   * Sets the final check items ack hash on a draft.
   * Called when user acknowledges settings mismatch via "Settings Changed" modal.
   *
   * If the hash is already equal to the stored value, returns true without write/notify (no-op).
   *
   * @param draftId - The draft ID to update
   * @param hash - The settings hash to store
   * @returns true if update succeeded or no-op, false if draft not found or tombstoned
   */
  async setFinalCheckItemsAckHash(draftId: string, hash: string): Promise<boolean> {
    return this._setAckHashField(draftId, 'finalCheckItemsAckHash', hash);
  }

  /**
   * Sets the trading setup ack hash on a draft.
   * Called when user acknowledges settings mismatch via "Settings Changed" modal.
   *
   * If the hash is already equal to the stored value, returns true without write/notify (no-op).
   *
   * @param draftId - The draft ID to update
   * @param hash - The settings hash to store
   * @returns true if update succeeded or no-op, false if draft not found or tombstoned
   */
  async setTradingSetupAckHash(draftId: string, hash: string): Promise<boolean> {
    return this._setAckHashField(draftId, 'tradingSetupAckHash', hash);
  }

  /**
   * Sets the ack hash for risk profile default divergence detection.
   * After setting, subsequent loads will not re-prompt for this default profile change.
   */
  async setRiskProfileDefaultAckHash(draftId: string, hash: string): Promise<boolean> {
    return this._setAckHashField(draftId, 'riskProfileDefaultAckHash', hash);
  }

  /**
   * Internal helper to set any ack hash field on a draft.
   * Handles load, no-op check, diff ops, transaction, and notification.
   */
  private async _setAckHashField(
    draftId: string,
    field:
      | 'healthMetricsAckHash'
      | 'personalPrepAckHash'
      | 'sectionVisibilityAckHash'
      | 'finalCheckItemsAckHash'
      | 'marketSnapshotFieldsAckHash'
      | 'tradingSetupAckHash'
      | 'riskProfileDefaultAckHash',
    hash: string
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      return false;
    }

    // No-op if hash is already equal
    if (existingDraft[field] === hash) {
      return true;
    }

    // Build candidate draft with new hash
    const candidateAfter: SessionDraft = {
      ...existingDraft,
      [field]: hash,
    };

    // Compute diff BEFORE touching metadata
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      candidateAfter as unknown as Record<string, unknown>
    );

    // If no actual changes (shouldn't happen given guard above), return early
    if (ops.length === 0) {
      return true;
    }

    // Add metadata.updatedAt op
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Updates only the sectionConfig in the pre-trading config snapshot.
   * Used to persist merged/normalized sectionConfig for legacy drafts or partial configs.
   *
   * This is a targeted patch that only affects sectionConfig, preserving all other
   * preTradingConfig fields (healthMetrics, personalPrepFields, etc.) unchanged.
   *
   * If no effective changes, returns true without write/notify (no-op).
   *
   * **Type-guard**: Requires preTradingConfig present (legacy fallback must be applied before use).
   *
   * @param draftId - The draft ID to update
   * @param sectionConfig - The normalized section configuration to store
   * @returns true if update succeeded or no-op, false if draft not found, tombstoned, or preTradingConfig undefined
   */
  async updateSectionConfig(
    draftId: string,
    sectionConfig: PreTradingSectionConfig[]
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      return false;
    }

    // Require preTradingConfig to be defined
    if (!existingDraft.preTradingConfig) {
      return false;
    }

    // Build candidate draft with updated sectionConfig
    const candidateAfter: SessionDraft = {
      ...existingDraft,
      preTradingConfig: {
        ...existingDraft.preTradingConfig,
        sectionConfig,
      },
    };

    // Compute diff BEFORE touching metadata
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      candidateAfter as unknown as Record<string, unknown>
    );

    // If no actual changes, return early (no-op)
    if (ops.length === 0) {
      return true;
    }

    // Add metadata.updatedAt op
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Updates only the health metrics section of the pre-trading config snapshot.
   * Used by workflow config modals to edit health metrics without affecting other sections.
   *
   * Migrates existing healthMetrics values by filtering to only IDs that exist in the new config.
   * If migrated map becomes empty, the root key is deleted (treat {} as undefined).
   * personalPrep values are preserved unchanged.
   * If no effective changes, returns true without write/notify (no-op).
   *
   * **Type-guard**: Requires preTradingConfig present (legacy fallback must be applied before use).
   *
   * @param draftId - The draft ID to update
   * @param healthMetricsSettings - The new health metrics configuration
   * @returns true if update succeeded or no-op, false if draft not found, tombstoned, or preTradingConfig undefined
   */
  async updateHealthMetricsConfig(
    draftId: string,
    healthMetricsSettings: PreTradingSettings['healthMetrics']
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      return false;
    }

    // Require preTradingConfig to be defined (legacy fallback should be applied before use)
    if (!existingDraft.preTradingConfig) {
      return false;
    }

    // Build new healthMetrics snapshot as new object (no mutation)
    const newHealthMetricsSnapshot: typeof existingDraft.preTradingConfig.healthMetrics = {
      source: healthMetricsSettings.source,
      metrics: healthMetricsSettings.metrics.map(m => ({ ...m })),
    };

    // Build merged config as new object (preserve other sections)
    const newConfig = {
      ...existingDraft.preTradingConfig,
      healthMetrics: newHealthMetricsSnapshot,
    };

    // Migrate healthMetrics values: keep only IDs that exist in new config
    const newMetricIds = new Set(newHealthMetricsSnapshot.metrics.map(m => m.id));
    const existingHealthMetrics = existingDraft.healthMetrics ?? {};
    const migratedHealthMetrics = Object.fromEntries(
      Object.entries(existingHealthMetrics).filter(([id]) => newMetricIds.has(id))
    );

    // Build candidate draft with new config + migrated healthMetrics
    // personalPrep values are preserved unchanged
    const candidateAfter: SessionDraft = { ...existingDraft };
    candidateAfter.preTradingConfig = newConfig;

    // Delete root key if migrated map is empty (treat {} as undefined)
    if (Object.keys(migratedHealthMetrics).length > 0) {
      candidateAfter.healthMetrics = migratedHealthMetrics;
    } else {
      delete candidateAfter.healthMetrics;
    }

    // For buildDiffOps, handle cleared sections as null for proper sync
    const afterForDiff: Record<string, unknown> = {
      ...(candidateAfter as unknown as Record<string, unknown>),
    };
    // If clearing healthMetrics (was defined, now empty), set null for diff
    if (
      Object.keys(migratedHealthMetrics).length === 0 &&
      existingDraft.healthMetrics !== undefined
    ) {
      afterForDiff['healthMetrics'] = null;
    }

    // Compute diff BEFORE touching metadata to avoid false positives
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      afterForDiff
    );

    // If no actual changes, return early (no-op)
    if (ops.length === 0) {
      return true;
    }

    // Since there ARE data changes, also sync metadata.updatedAt
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Updates only the personal prep fields section of the pre-trading config snapshot.
   * Used by workflow config modals to edit personal prep fields without affecting other sections.
   *
   * Migrates existing personalPrep values by filtering to only IDs that exist in the new config,
   * have the same type as the old config, and (for dropdowns) have a valid option ID.
   * If migrated map becomes empty, the root key is deleted (treat {} as undefined).
   * healthMetrics values are preserved unchanged.
   * If no effective changes, returns true without write/notify (no-op).
   *
   * **Type-guard**: Requires preTradingConfig present (legacy fallback must be applied before use).
   *
   * @param draftId - The draft ID to update
   * @param personalPrepFieldsSettings - The new personal prep fields configuration
   * @returns true if update succeeded or no-op, false if draft not found, tombstoned, or preTradingConfig undefined
   */
  async updatePersonalPrepFieldsConfig(
    draftId: string,
    personalPrepFieldsSettings: PreTradingSettings['personalPrepFields']
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      return false;
    }

    // Require preTradingConfig to be defined (legacy fallback should be applied before use)
    if (!existingDraft.preTradingConfig) {
      return false;
    }

    // Build new personalPrepFields snapshot as new object with deep copy
    const newFieldsSnapshot: typeof existingDraft.preTradingConfig.personalPrepFields =
      personalPrepFieldsSettings.map(f => {
        const field: typeof existingDraft.preTradingConfig.personalPrepFields[number] = {
          id: f.id,
          name: f.name,
          type: f.type,
        };
        if (f.description !== undefined) {
          field.description = f.description;
        }
        if (f.options) {
          field.options = f.options.map(o => ({ ...o }));
        }
        return field;
      });

    // Build merged config as new object (preserve other sections)
    const newConfig = {
      ...existingDraft.preTradingConfig,
      personalPrepFields: newFieldsSnapshot,
    };

    // Migrate personalPrep values: keep only IDs that exist in new config AND have same type
    // Type changes invalidate values (e.g., text → dropdown makes old value invalid)
    // For dropdowns, also check that the stored option ID still exists in new options
    const newFieldsById = new Map(newFieldsSnapshot.map(f => [f.id, f]));
    const oldFieldsById = new Map(
      existingDraft.preTradingConfig.personalPrepFields.map(f => [f.id, f])
    );
    const existingPersonalPrep = existingDraft.personalPrep ?? {};
    const migratedPersonalPrep = Object.fromEntries(
      Object.entries(existingPersonalPrep).filter(([id, value]) => {
        const newField = newFieldsById.get(id);
        const oldField = oldFieldsById.get(id);
        // Keep value only if field exists in new config AND type hasn't changed
        if (!newField || !oldField || newField.type !== oldField.type) {
          return false;
        }
        // For dropdowns, check that the stored option ID still exists
        if (newField.type === 'dropdown' && typeof value === 'string') {
          const newOptionIds = new Set((newField.options ?? []).map(o => o.id));
          if (!newOptionIds.has(value)) {
            return false;
          }
        }
        return true;
      })
    );

    // Build candidate draft with new config + migrated personalPrep
    // healthMetrics values are preserved unchanged
    const candidateAfter: SessionDraft = { ...existingDraft };
    candidateAfter.preTradingConfig = newConfig;

    // Delete root key if migrated map is empty (treat {} as undefined)
    if (Object.keys(migratedPersonalPrep).length > 0) {
      candidateAfter.personalPrep = migratedPersonalPrep;
    } else {
      delete candidateAfter.personalPrep;
    }

    // For buildDiffOps, handle cleared sections as null for proper sync
    const afterForDiff: Record<string, unknown> = {
      ...(candidateAfter as unknown as Record<string, unknown>),
    };
    // If clearing personalPrep (was defined, now empty), set null for diff
    if (
      Object.keys(migratedPersonalPrep).length === 0 &&
      existingDraft.personalPrep !== undefined
    ) {
      afterForDiff['personalPrep'] = null;
    }

    // Compute diff BEFORE touching metadata to avoid false positives
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      afterForDiff
    );

    // If no actual changes, return early (no-op)
    if (ops.length === 0) {
      return true;
    }

    // Since there ARE data changes, also sync metadata.updatedAt
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Updates only the final check items section of the pre-trading config snapshot.
   * Used by SettingsChangedModal "Apply new settings" to update final checks config.
   *
   * Migrates existing finalChecks values by:
   * - Keeping only entries whose checkItemId exists in the new config
   * - Refreshing labels from the new config (update label field to match current settings)
   * - Preserving completed status for matched items
   *
   * If migrated array becomes empty, the root key is deleted (treat [] as undefined).
   * If no effective changes, returns true without write/notify (no-op).
   *
   * **Type-guard**: Requires preTradingConfig present (legacy fallback must be applied before use).
   *
   * @param draftId - The draft ID to update
   * @param finalCheckItemsSettings - The new final check items configuration
   * @returns true if update succeeded or no-op, false if draft not found, tombstoned, or preTradingConfig undefined
   */
  async updateFinalCheckItemsConfig(
    draftId: string,
    finalCheckItemsSettings: PreTradingSettings['finalCheckItems']
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      return false;
    }

    // Require preTradingConfig to be defined (legacy fallback should be applied before use)
    if (!existingDraft.preTradingConfig) {
      return false;
    }

    // Build new finalCheckItems snapshot as new object (no mutation)
    const newFinalCheckItemsSnapshot = finalCheckItemsSettings.map((item) => ({
      id: item.id,
      label: item.label,
    }));

    // Build merged config as new object (preserve other sections)
    const newConfig = {
      ...existingDraft.preTradingConfig,
      finalCheckItems: newFinalCheckItemsSnapshot,
    };

    // Migrate finalChecks values: keep only IDs that exist in new config, refresh labels
    const newItemsById = new Map(newFinalCheckItemsSnapshot.map((item) => [item.id, item]));
    const existingFinalChecks = existingDraft.finalChecks ?? [];
    const migratedFinalChecks: typeof existingFinalChecks = [];
    for (const entry of existingFinalChecks) {
      const newItem = newItemsById.get(entry.checkItemId);
      if (newItem) {
        migratedFinalChecks.push({
          checkItemId: entry.checkItemId,
          label: newItem.label, // Refresh label from new config
          completed: entry.completed, // Preserve completed status
        });
      }
    }

    // Build candidate draft with new config + migrated finalChecks
    const candidateAfter: SessionDraft = { ...existingDraft };
    candidateAfter.preTradingConfig = newConfig;

    // Delete root key if migrated array is empty (treat [] as undefined)
    if (migratedFinalChecks.length > 0) {
      candidateAfter.finalChecks = migratedFinalChecks;
    } else {
      delete candidateAfter.finalChecks;
    }

    // For buildDiffOps, handle cleared sections as null for proper sync
    const afterForDiff: Record<string, unknown> = {
      ...(candidateAfter as unknown as Record<string, unknown>),
    };
    // If clearing finalChecks (was defined, now empty), set null for diff
    if (migratedFinalChecks.length === 0 && existingDraft.finalChecks !== undefined) {
      afterForDiff['finalChecks'] = null;
    }

    // Compute diff BEFORE touching metadata to avoid false positives
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      afterForDiff
    );

    // If no actual changes, return early (no-op)
    if (ops.length === 0) {
      return true;
    }

    // Since there ARE data changes, also sync metadata.updatedAt
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Updates the maxObjectives in the pre-trading config snapshot.
   * Used by workflow config modal to edit how many objectives can be selected.
   *
   * Note: pinObjectivesToLive is NOT in PreTradingConfigSnapshot (it's a UX preference
   * that doesn't affect captured data). That setting is global-only and updated via
   * preTradingSettingsService.update() directly.
   *
   * Does NOT affect the objectives array (selected objectives) - only the config snapshot.
   * If no effective changes, returns true without write/notify (no-op).
   *
   * **Type-guard**: Requires preTradingConfig present (legacy fallback must be applied before use).
   *
   * @param draftId - The draft ID to update
   * @param maxObjectives - The new maximum objectives setting
   * @returns true if update succeeded or no-op, false if draft not found, tombstoned, or preTradingConfig undefined
   */
  async updateObjectivesConfig(
    draftId: string,
    maxObjectives: number
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      return false;
    }

    // Require preTradingConfig to be defined (legacy fallback should be applied before use)
    if (!existingDraft.preTradingConfig) {
      return false;
    }

    // Build merged config as new object (preserve other sections)
    const newConfig = {
      ...existingDraft.preTradingConfig,
      maxObjectives,
    };

    // Build candidate draft with new config
    const candidateAfter: SessionDraft = {
      ...existingDraft,
      preTradingConfig: newConfig,
    };

    // Compute diff BEFORE touching metadata to avoid false positives
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      candidateAfter as unknown as Record<string, unknown>
    );

    // If no actual changes, return early (no-op)
    if (ops.length === 0) {
      return true;
    }

    // Since there ARE data changes, also sync metadata.updatedAt
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Update marketStates array on a session draft
   *
   * Uses diff-based operation logging to only record actual changes.
   * Skips write entirely if no changes detected (avoids sync churn).
   * Always sets marketStates to the provided array (never deletes the field).
   *
   * @param draftId - The draft ID to update
   * @param marketStates - The new marketStates array (can be empty)
   * @returns true if update succeeded or no-op, false if draft not found
   */
  async updateMarketStates(
    draftId: string,
    marketStates: MarketStateDraft[]
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      // Draft not found or was soft-deleted - return false to trigger redirect
      return false;
    }

    // Build candidate draft with new marketStates array
    const candidateAfter: SessionDraft = {
      ...existingDraft,
      marketStates,
    };

    // For diff computation, treat undefined as [] to handle legacy drafts
    const existingForDiff: Record<string, unknown> = {
      ...(existingDraft as unknown as Record<string, unknown>),
      marketStates: existingDraft.marketStates ?? [],
    };

    // Compute diff BEFORE touching metadata to avoid false positives
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingForDiff,
      candidateAfter as unknown as Record<string, unknown>
    );

    // If no actual changes, return early (skip write entirely)
    if (ops.length === 0) {
      return true;
    }

    // Since there ARE data changes, also sync metadata.updatedAt
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Updates the trading setup fields on a SessionDraft.
   * Used by PreTradingPhase auto-save for the trading setup workflow step.
   *
   * `setupConfirmed` is an independent step-level flag.
   * `riskSnapshot` is passed through as-is (lifecycle managed by T09 risk confirmation).
   *
   * Syncs plannedAccountIds with defaultAccountId for backward compatibility.
   *
   * If no effective changes, returns true without write/notify (no-op).
   *
   * @param draftId - The draft ID to update
   * @param activePlaybookSelections - Strategy-playbook pairs the trader intends to use
   * @param defaultAccountId - Default account for execution prefill (undefined = no account)
   * @param focusInstrumentIds - Instruments the trader intends to focus on (undefined = none)
   * @param riskSnapshot - Risk snapshot at intent-declaration time (lifecycle managed by T09)
   * @param setupConfirmed - Whether the trading setup has been explicitly confirmed
   * @param selectedRiskProfileId - ID of the selected risk profile (undefined = no profile)
   * @param selectedRiskProfileSource - Provenance: 'default' (auto-resolved) or 'manual' (user-picked)
   * @returns true if update succeeded or no-op, false if draft not found or tombstoned
   */
  async updateTradingSetup(
    draftId: string,
    activePlaybookSelections: StrategyPlaybookPair[],
    defaultAccountId: string | undefined,
    focusInstrumentIds: string[] | undefined,
    riskSnapshot: TradingSetupRiskSnapshot | undefined,
    setupConfirmed: boolean,
    selectedRiskProfileId: string | undefined,
    selectedRiskProfileSource: 'default' | 'manual' | undefined,
    sessionStrategyOverrides: SessionStrategyOverride[] | undefined,
    sessionPlaybookOverrides: SessionPlaybookOverride[] | undefined
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      return false;
    }

    // Build candidate draft with new trading setup fields
    const candidateAfter: SessionDraft = { ...existingDraft };

    // activePlaybookSelections: always set (required array)
    candidateAfter.activePlaybookSelections = activePlaybookSelections;

    // setupConfirmed: always set (independent step-level flag)
    candidateAfter.setupConfirmed = setupConfirmed;

    // defaultAccountId: set if provided, delete if cleared
    if (defaultAccountId !== undefined) {
      candidateAfter.defaultAccountId = defaultAccountId;
      candidateAfter.plannedAccountIds = [defaultAccountId];
    } else {
      delete candidateAfter.defaultAccountId;
      delete candidateAfter.plannedAccountIds;
    }

    // focusInstrumentIds: set if provided, delete if cleared
    if (focusInstrumentIds !== undefined) {
      candidateAfter.focusInstrumentIds = focusInstrumentIds;
    } else {
      delete candidateAfter.focusInstrumentIds;
    }

    // riskSnapshot: simple pass-through (lifecycle managed by T09 risk confirmation)
    if (riskSnapshot !== undefined) {
      candidateAfter.riskSnapshot = riskSnapshot;
    } else {
      delete candidateAfter.riskSnapshot;
    }

    // selectedRiskProfileId: set if provided, delete if cleared
    if (selectedRiskProfileId !== undefined) {
      candidateAfter.selectedRiskProfileId = selectedRiskProfileId;
    } else {
      delete candidateAfter.selectedRiskProfileId;
    }

    // selectedRiskProfileSource: set if provided, delete if cleared
    if (selectedRiskProfileSource !== undefined) {
      candidateAfter.selectedRiskProfileSource = selectedRiskProfileSource;
    } else {
      delete candidateAfter.selectedRiskProfileSource;
    }

    // sessionStrategyOverrides: set if provided, delete if cleared
    if (sessionStrategyOverrides !== undefined) {
      candidateAfter.sessionStrategyOverrides = sessionStrategyOverrides;
    } else {
      delete candidateAfter.sessionStrategyOverrides;
    }

    // sessionPlaybookOverrides: set if provided, delete if cleared
    if (sessionPlaybookOverrides !== undefined) {
      candidateAfter.sessionPlaybookOverrides = sessionPlaybookOverrides;
    } else {
      delete candidateAfter.sessionPlaybookOverrides;
    }

    // Build shadow object for diff with null-signaling for cleared fields
    const afterForDiff: Record<string, unknown> = {
      ...(candidateAfter as unknown as Record<string, unknown>),
    };
    // Signal field clearing to sync engine via null
    if (defaultAccountId === undefined && existingDraft.defaultAccountId !== undefined) {
      afterForDiff['defaultAccountId'] = null;
    }
    if (defaultAccountId === undefined && existingDraft.plannedAccountIds !== undefined) {
      afterForDiff['plannedAccountIds'] = null;
    }
    if (focusInstrumentIds === undefined && existingDraft.focusInstrumentIds !== undefined) {
      afterForDiff['focusInstrumentIds'] = null;
    }
    if (riskSnapshot === undefined && existingDraft.riskSnapshot !== undefined) {
      afterForDiff['riskSnapshot'] = null;
    }
    if (selectedRiskProfileId === undefined && existingDraft.selectedRiskProfileId !== undefined) {
      afterForDiff['selectedRiskProfileId'] = null;
    }
    if (selectedRiskProfileSource === undefined && existingDraft.selectedRiskProfileSource !== undefined) {
      afterForDiff['selectedRiskProfileSource'] = null;
    }
    if (sessionStrategyOverrides === undefined && existingDraft.sessionStrategyOverrides !== undefined) {
      afterForDiff['sessionStrategyOverrides'] = null;
    }
    if (sessionPlaybookOverrides === undefined && existingDraft.sessionPlaybookOverrides !== undefined) {
      afterForDiff['sessionPlaybookOverrides'] = null;
    }
    // Compute diff BEFORE touching metadata to avoid false positives
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      afterForDiff
    );

    // If no actual changes, return early (skip write entirely)
    if (ops.length === 0) {
      return true;
    }

    // Since there ARE data changes, also sync metadata.updatedAt
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Sets the market snapshot fields ack hash on a draft.
   * Called when user acknowledges field config via MarketSnapshotFieldsConfigModal.
   *
   * If the hash is already equal to the stored value, returns true without write/notify (no-op).
   *
   * @param draftId - The draft ID to update
   * @param hash - The fields hash to store
   * @returns true if update succeeded or no-op, false if draft not found or tombstoned
   */
  async setMarketSnapshotFieldsAckHash(draftId: string, hash: string): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      return false;
    }

    // No-op if hash is already equal
    if (existingDraft.marketSnapshotFieldsAckHash === hash) {
      return true;
    }

    // Build candidate draft with new hash
    const candidateAfter: SessionDraft = {
      ...existingDraft,
      marketSnapshotFieldsAckHash: hash,
    };

    // Compute diff BEFORE touching metadata
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      candidateAfter as unknown as Record<string, unknown>
    );

    // If no actual changes (shouldn't happen given guard above), return early
    if (ops.length === 0) {
      return true;
    }

    // Add metadata.updatedAt op
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Updates the fieldConfigSnapshot in ALL MarketStateDraft entries.
   * Used when field config changes via MarketSnapshotFieldsConfigModal.
   *
   * Mutates on each entry:
   * - fieldConfigSnapshot: replaced with new config
   * - updatedAt: set to current timestamp
   * - snapshots[].data: purges orphaned data for field IDs not in new config
   *
   * Preserves: id, createdAt, instrumentSnapshot, timeframe, snapshots[].type, snapshots[].capturedAt
   *
   * If marketStates is undefined or empty, returns true (no-op).
   * If no effective changes, returns true without write/notify (no-op).
   *
   * @param draftId - The draft ID to update
   * @param fieldConfigSnapshot - The new field config to apply to all market state entries
   * @returns true if update succeeded or no-op, false if draft not found or tombstoned
   */
  async updateMarketStatesFieldConfig(
    draftId: string,
    fieldConfigSnapshot: MarketStateDraft['fieldConfigSnapshot']
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      return false;
    }

    // Guard: marketStates undefined or empty → no-op
    if (!existingDraft.marketStates || existingDraft.marketStates.length === 0) {
      return true;
    }

    // Build updated marketStates with new fieldConfigSnapshot, purged data, and updatedAt
    const now = new Date().toISOString();

    // Extract valid field IDs for data purging
    const validFieldIds = new Set(fieldConfigSnapshot.map(f => f.fieldId));

    const updatedMarketStates: MarketStateDraft[] = existingDraft.marketStates.map(entry => ({
      ...entry,
      fieldConfigSnapshot,
      updatedAt: now,
      // Purge orphaned data from snapshots (fields that no longer exist)
      snapshots: entry.snapshots.map(snapshot => ({
        ...snapshot,
        // Filter data to only include valid field IDs
        // Skip filter if data is empty to avoid unnecessary object creation
        data:
          Object.keys(snapshot.data).length === 0
            ? snapshot.data
            : Object.fromEntries(
                Object.entries(snapshot.data).filter(([fieldId]) => validFieldIds.has(fieldId))
              ),
      })),
    }));

    // Build candidate draft with updated marketStates
    const candidateAfter: SessionDraft = {
      ...existingDraft,
      marketStates: updatedMarketStates,
    };

    // Compute diff BEFORE touching metadata
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      candidateAfter as unknown as Record<string, unknown>
    );

    // If no actual changes, return early (no-op)
    if (ops.length === 0) {
      return true;
    }

    // Add metadata.updatedAt op on SessionDraft
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Sets transitionScreenUsed to true on a session draft.
   * Called when the transition screen modal is displayed before entering live trading.
   *
   * If already set to true, returns true without writing or notifying (no-op).
   * This handles scenarios where the method may be called multiple times on resume/re-open.
   *
   * @param draftId - The draft ID to update
   * @returns true if update succeeded or no-op, false if draft not found or tombstoned
   */
  async updateTransitionScreenUsed(draftId: string): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      return false;
    }

    // No-op if already set to true
    if (existingDraft.transitionScreenUsed === true) {
      return true;
    }

    // Build candidate draft with transitionScreenUsed set to true
    const candidateAfter: SessionDraft = {
      ...existingDraft,
      transitionScreenUsed: true,
    };

    // Compute diff BEFORE touching metadata
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      candidateAfter as unknown as Record<string, unknown>
    );

    // If no actual changes (shouldn't happen given guard above), return early
    if (ops.length === 0) {
      return true;
    }

    // Add metadata.updatedAt op
    const now = new Date().toISOString();
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }
  /**
   * Allowed status transitions for the SessionDraft phase state machine.
   *
   * Transition rules:
   * - pre-trading → live: Start live trading session
   * - live → pre-trading: Return to pre-trading (backward, no timer reset)
   * - live → post-trading: End trading session
   * - live → shelved: Shelve session for later
   * - post-trading → review: S03 scope (included for completeness)
   *
   * NOT allowed:
   * - post-trading → live: Session is closed for trading
   * - review → any: Terminal read-only state
   * - shelved → any: Must be unshelved via separate flow
   */
  private static readonly ALLOWED_TRANSITIONS: ReadonlyMap<
    SessionDraft['status'],
    ReadonlySet<SessionDraft['status']>
  > = new Map([
    ['pre-trading', new Set(['live'])],
    ['live', new Set(['pre-trading', 'post-trading', 'shelved'])],
    ['post-trading', new Set(['review'])],
    ['review', new Set<SessionDraft['status']>()],
    ['shelved', new Set<SessionDraft['status']>()],
  ]);

  /**
   * Check whether a status transition is allowed by the state machine.
   *
   * @param from - Current status
   * @param to - Target status
   * @returns true if the transition is valid
   */
  isTransitionAllowed(
    from: SessionDraft['status'],
    to: SessionDraft['status']
  ): boolean {
    const allowed = SessionDraftService.ALLOWED_TRANSITIONS.get(from);
    return allowed?.has(to) ?? false;
  }

  /**
   * Update the session draft status (phase transition).
   *
   * Validates the transition against the state machine before persisting.
   * Callers are responsible for flushing pending autosaves and showing
   * confirmation UX BEFORE calling this method.
   *
   * @param draftId - The draft ID to update
   * @param newStatus - The target status
   * @returns true if the update succeeded, false if draft not found or transition invalid
   */
  async updateStatus(
    draftId: string,
    newStatus: SessionDraft['status']
  ): Promise<boolean> {
    // Load existing draft
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      return false;
    }

    // No-op if already at target status
    if (existingDraft.status === newStatus) {
      return true;
    }

    // Validate transition
    if (!this.isTransitionAllowed(existingDraft.status, newStatus)) {
      console.warn(
        `[SessionDraftService] Invalid status transition: ${existingDraft.status} → ${newStatus}`
      );
      return false;
    }

    // --- Phase timing: compute departing phase elapsed ---
    const now = new Date().toISOString();
    const nowMs = new Date(now).getTime();
    const departingStatus = existingDraft.status as TimedPhaseStatus; // safe: shelved → ∅

    let updatedPhaseElapsedMs = existingDraft.phaseElapsedMs;

    if (existingDraft.currentPhaseStartedAt) {
      const startMs = new Date(existingDraft.currentPhaseStartedAt).getTime();
      if (Number.isFinite(startMs)) {
        const elapsed = nowMs - startMs;
        const accumulated =
          (existingDraft.phaseElapsedMs?.[departingStatus] ?? 0) + Math.max(0, elapsed);
        updatedPhaseElapsedMs = {
          ...existingDraft.phaseElapsedMs,
          [departingStatus]: accumulated,
        };
      }
    }

    // --- Phase timing: set arriving phase ---
    // Build candidate with status + timing fields
    const candidateAfter: SessionDraft = {
      ...existingDraft,
      status: newStatus,
      ...(updatedPhaseElapsedMs !== undefined ? { phaseElapsedMs: updatedPhaseElapsedMs } : {}),
      ...(newStatus !== 'shelved' ? { currentPhaseStartedAt: now } : {}),
    };

    // If shelved, clear currentPhaseStartedAt from candidate
    if (newStatus === 'shelved') {
      delete candidateAfter.currentPhaseStartedAt;
    }

    // Build diff object — set null for cleared fields so buildDiffOps generates clear ops
    const afterForDiff: Record<string, unknown> = {
      ...(candidateAfter as unknown as Record<string, unknown>),
    };
    if (newStatus === 'shelved' && existingDraft.currentPhaseStartedAt !== undefined) {
      afterForDiff['currentPhaseStartedAt'] = null;
    }

    // Compute diff BEFORE touching metadata
    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      afterForDiff
    );

    // If no actual changes (shouldn't happen given guard above), return early
    if (ops.length === 0) {
      return true;
    }

    // Add metadata.updatedAt op
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    // Build final draft with updated timestamp
    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    // Wrap in transaction with op logging
    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    // Notify after transaction commits
    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Flush the current phase's elapsed time into phaseElapsedMs and clear
   * currentPhaseStartedAt. Called on hub exit (Save & Exit, navigation away,
   * visibilitychange/pagehide).
   *
   * No-op if currentPhaseStartedAt is not set. For shelved drafts, clears
   * stale currentPhaseStartedAt defensively without accumulating time.
   *
   * @param draftId - The draft ID to flush timing for
   * @returns true if flush succeeded or no-op, false if draft not found
   */
  async flushPhaseElapsed(draftId: string): Promise<boolean> {
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      return false;
    }

    // Nothing to flush if no active phase timer
    if (!existingDraft.currentPhaseStartedAt) {
      return true;
    }

    const now = new Date().toISOString();

    // Build candidate — always clear currentPhaseStartedAt
    const candidateAfter: SessionDraft = { ...existingDraft };
    delete candidateAfter.currentPhaseStartedAt;

    // Accumulate elapsed time only for non-shelved drafts with valid timestamps
    if (existingDraft.status !== 'shelved') {
      const startMs = new Date(existingDraft.currentPhaseStartedAt).getTime();
      if (Number.isFinite(startMs)) {
        const timedStatus = existingDraft.status as TimedPhaseStatus;
        const elapsed = Date.now() - startMs;
        const accumulated =
          (existingDraft.phaseElapsedMs?.[timedStatus] ?? 0) + Math.max(0, elapsed);
        candidateAfter.phaseElapsedMs = {
          ...existingDraft.phaseElapsedMs,
          [timedStatus]: accumulated,
        };
      }
    }

    // Build diff object with null for cleared field
    const afterForDiff: Record<string, unknown> = {
      ...(candidateAfter as unknown as Record<string, unknown>),
      currentPhaseStartedAt: null,
    };

    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      afterForDiff
    );

    if (ops.length === 0) {
      return true;
    }

    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  /**
   * Set currentPhaseStartedAt to now for the current phase. If a stale
   * currentPhaseStartedAt exists (e.g., from a failed best-effort flush),
   * the elapsed time is recovered into phaseElapsedMs before stamping fresh.
   *
   * Called on TradingHub entry/re-entry. For shelved drafts, clears stale
   * currentPhaseStartedAt defensively without accumulating time or stamping.
   *
   * @param draftId - The draft ID to stamp
   * @returns true if stamp succeeded or no-op, false if draft not found
   */
  async stampPhaseStart(draftId: string): Promise<boolean> {
    const existingDraft = await this.table.get(draftId);
    if (!existingDraft || isTombstoned(existingDraft)) {
      return false;
    }

    const now = new Date().toISOString();
    const candidateAfter: SessionDraft = { ...existingDraft };

    // Shelved drafts: only clear stale currentPhaseStartedAt if present
    if (existingDraft.status === 'shelved') {
      if (!existingDraft.currentPhaseStartedAt) {
        return true; // nothing to clear, no-op
      }
      delete candidateAfter.currentPhaseStartedAt;

      const afterForDiff: Record<string, unknown> = {
        ...(candidateAfter as unknown as Record<string, unknown>),
        currentPhaseStartedAt: null,
      };

      const ops = buildDiffOps(
        'session_drafts',
        draftId,
        existingDraft as unknown as Record<string, unknown>,
        afterForDiff
      );

      if (ops.length === 0) {
        return true;
      }

      ops.push({
        table: 'session_drafts',
        recordId: draftId,
        op: 'set',
        field: 'metadata.updatedAt',
        value: now,
      });

      const finalDraft: SessionDraft = {
        ...candidateAfter,
        metadata: {
          ...candidateAfter.metadata,
          updatedAt: now,
        },
      };

      await db.transaction(
        'rw',
        [db.session_drafts, db.sync_operations, db.sync_state],
        async () => {
          markAsInstrumentedWriteContext();
          await this.table.put(finalDraft);
          await getSyncOperationService().logBatch(ops);
        }
      );

      syncCoordinatorService.notifyChange('session_drafts', draftId);
      return true;
    }

    // Non-shelved: recover stale elapsed if currentPhaseStartedAt exists
    if (existingDraft.currentPhaseStartedAt) {
      const startMs = new Date(existingDraft.currentPhaseStartedAt).getTime();
      if (Number.isFinite(startMs)) {
        const timedStatus = existingDraft.status as TimedPhaseStatus;
        const elapsed = Date.now() - startMs;
        const accumulated =
          (existingDraft.phaseElapsedMs?.[timedStatus] ?? 0) + Math.max(0, elapsed);
        candidateAfter.phaseElapsedMs = {
          ...existingDraft.phaseElapsedMs,
          [timedStatus]: accumulated,
        };
      }
    }

    // Stamp fresh
    candidateAfter.currentPhaseStartedAt = now;

    const ops = buildDiffOps(
      'session_drafts',
      draftId,
      existingDraft as unknown as Record<string, unknown>,
      candidateAfter as unknown as Record<string, unknown>
    );

    // For non-shelved drafts, will always produce at least one op (currentPhaseStartedAt set)
    ops.push({
      table: 'session_drafts',
      recordId: draftId,
      op: 'set',
      field: 'metadata.updatedAt',
      value: now,
    });

    const finalDraft: SessionDraft = {
      ...candidateAfter,
      metadata: {
        ...candidateAfter.metadata,
        updatedAt: now,
      },
    };

    await db.transaction(
      'rw',
      [db.session_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();
        await this.table.put(finalDraft);
        await getSyncOperationService().logBatch(ops);
      }
    );

    syncCoordinatorService.notifyChange('session_drafts', draftId);

    return true;
  }

  // ===== CARRY-FORWARD =====

  /**
   * Returns all eligible carry-forward trade setup draft candidates for session-entry review.
   *
   * Applies 7 rules:
   *   1. Session drafts must not be tombstoned
   *   2. Skips the current session (its own trades are live, not candidates)
   *   3. Individual trade drafts must not be tombstoned (promoted in-place by TradePromotionService)
   *   4. `artifactKind` must be 'trade' (opportunity drafts are not carried forward)
   *   5. `carryForwardDecision` must be 'carry-forward' (user resolved at session close)
   *   6. `rootDraftId` must not be in the retired lineages set ("Delete Forever")
   *   7. `rootDraftId` must not already be present in the current session
   *
   * After collecting all passing candidates, deduplicates by rootDraftId keeping the
   * newest node per lineage (highest `createdAt`) — prevents older ancestor nodes from
   * surfacing alongside or instead of the latest carry-forward node.
   *
   * @param currentSessionDraftId - The active session draft ID (excluded from candidate source)
   * @returns Deduplicated array of eligible TradeSetupDraft candidates
   */
  async getPendingCarryForwardDrafts(currentSessionDraftId: string): Promise<TradeSetupDraft[]> {
    const allSessions = await this.table.toArray();
    const activeSessions = allSessions.filter((s) => !isTombstoned(s));

    // Build set of rootDraftIds already present in the current session
    const currentSession = activeSessions.find((s) => s.id === currentSessionDraftId);
    const currentRootDraftIds = new Set<string>(
      (currentSession?.trades ?? [])
        .filter((t) => !isTombstoned(t) && !!t.rootDraftId)
        .map((t) => t.rootDraftId)
    );

    const retiredRootDraftIds = await retiredDraftLineageService.getRetiredRootDraftIds();

    // Collect candidates from all other sessions
    const candidates: TradeSetupDraft[] = [];
    for (const session of activeSessions) {
      if (session.id === currentSessionDraftId) continue;
      for (const trade of session.trades ?? []) {
        if (isTombstoned(trade)) continue;
        if (trade.artifactKind !== 'trade') continue;
        if (!trade.rootDraftId) continue; // pre-WU2A drafts lack rootDraftId — not valid carry-forward candidates
        if (trade.carryForwardDecision !== 'carry-forward') continue;
        if (retiredRootDraftIds.has(trade.rootDraftId)) continue;
        if (currentRootDraftIds.has(trade.rootDraftId)) continue;
        candidates.push(trade as TradeSetupDraft);
      }
    }

    // Dedup by rootDraftId — keep newest createdAt node per lineage
    const byRootDraftId = new Map<string, TradeSetupDraft>();
    for (const candidate of candidates) {
      const existing = byRootDraftId.get(candidate.rootDraftId);
      if (!existing || candidate.createdAt > existing.createdAt) {
        byRootDraftId.set(candidate.rootDraftId, candidate);
      }
    }

    return Array.from(byRootDraftId.values());
  }

  /**
   * Materializes a carry-forward draft into the current session.
   *
   * Runs a single Dexie transaction over session_drafts, note_drafts, sync_operations,
   * and sync_state. Rolls back entirely if the session draft is not found.
   *
   * Steps (inside transaction):
   *   1. Load existingSession first; idempotency guard — if rootDraftId already present,
   *      return existing draft immediately (cross-tab concurrent materialization safety)
   *   2. Clone NoteDraft records (raw db.note_drafts.add — BaseService.add() hooks are
   *      unsafe inside an outer transaction; deletedAt excluded from clone)
   *   3. Append new draft to session; buildDiffOps + metadata.updatedAt + put + logBatch
   *
   * Post-transaction: notifyChange('session_drafts') only on actual write — notes loaded lazily.
   *
   * @param source - The carry-forward candidate to materialize
   * @param currentSessionDraftId - The session to receive the new draft node
   * @returns The newly created TradeSetupDraft
   * @throws If the current session draft is not found (transaction rolled back)
   */
  async materializeCarryForwardDraft(
    source: TradeSetupDraft,
    currentSessionDraftId: string,
  ): Promise<TradeSetupDraft> {
    const now = new Date().toISOString();

    const newDraft: TradeSetupDraft = {
      id: generateUUID(),
      rootDraftId: source.rootDraftId,
      sessionDraftId: currentSessionDraftId,
      originSessionDraftId: source.originSessionDraftId,
      carriedFromDraftId: source.id,
      createdAt: now,
      updatedAt: now,
      artifactKind: 'trade',
      workflowStatus: 'pending',
      isShelved: false,
      carryForwardDecision: 'undecided',
      phase1: source.phase1,
      ...(source.tradeName !== undefined ? { tradeName: source.tradeName } : {}),
      ...(source.accountId !== undefined ? { accountId: source.accountId } : {}),
    };

    let materializedDraft: TradeSetupDraft | undefined;

    await db.transaction(
      'rw',
      [db.session_drafts, db.note_drafts, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();

        // Step 1 (inside transaction) — Load session first; guard against concurrent materialization
        const existingSession = await db.session_drafts.get(currentSessionDraftId);
        if (!existingSession) {
          throw new Error(
            `Session draft ${currentSessionDraftId} not found — cannot materialize carry-forward draft`,
          );
        }

        // Idempotency guard: if this lineage was already materialized (cross-tab race),
        // return the existing draft without writing anything new.
        const match = (existingSession.trades ?? []).find(
          (t) => !isTombstoned(t) && t.rootDraftId === source.rootDraftId,
        );
        if (match) {
          materializedDraft = match as TradeSetupDraft;
          return; // exit transaction callback early — no writes needed
        }

        // Step 2 — Clone NoteDraft records for the source draft
        const sourceNotes = await db.note_drafts
          .where('[noteType+ownerId]')
          .equals(['trade', source.id])
          .toArray();

        for (const note of sourceNotes) {
          if (isTombstoned(note)) continue;
          // Destructure managed fields to prevent them carrying over to the clone
          const { deletedAt: _d, id: _i, createdAt: _ca, updatedAt: _ua, ownerId: _oi, ...noteContent } = note;
          const newNote: NoteDraft = {
            ...noteContent,
            id: generateUUID(),
            createdAt: now,
            updatedAt: now,
            ownerId: newDraft.id,
          };
          await db.note_drafts.add(newNote);
          const noteOps = buildSetOps(
            'note_drafts',
            newNote.id,
            newNote as unknown as Record<string, unknown>,
          );
          if (noteOps.length > 0) {
            await getSyncOperationService().logBatch(noteOps);
          }
        }

        // Step 3 — Append new draft to current session
        const updatedTrades: TradeDraft[] = [...(existingSession.trades ?? []), newDraft];
        const candidateAfter: SessionDraft = {
          ...existingSession,
          trades: updatedTrades,
        };

        // Compute diff before touching metadata (same pattern as updateTrades)
        const ops = buildDiffOps(
          'session_drafts',
          currentSessionDraftId,
          existingSession as unknown as Record<string, unknown>,
          candidateAfter as unknown as Record<string, unknown>,
        );

        // Manually append metadata.updatedAt op
        ops.push({
          table: 'session_drafts',
          recordId: currentSessionDraftId,
          op: 'set',
          field: 'metadata.updatedAt',
          value: now,
        });

        const finalSession: SessionDraft = {
          ...candidateAfter,
          metadata: {
            ...candidateAfter.metadata,
            updatedAt: now,
          },
        };

        await db.session_drafts.put(finalSession);
        await getSyncOperationService().logBatch(ops);
      },
    );

    // Already materialized in a concurrent tab — return existing draft, skip notify
    if (materializedDraft !== undefined) return materializedDraft;

    // Notify after transaction commits — notes are loaded lazily, no note notify needed
    syncCoordinatorService.notifyChange('session_drafts', currentSessionDraftId);

    return newDraft;
  }
}

// Export singleton instance
export const sessionDraftService = new SessionDraftService();
